import React from 'react'
import cn from 'classnames'
import FormattedCurrency from 'js/components/FormattedCurrency'
import { FormattedMessage } from 'react-intl'
import ChooseSubscriptionText from './ChooseSubscriptionPriceText'

interface DurationProps {
  duration: 1 | 3 | 6 | 12 | 24
  discount: number
  selected: boolean
  price?: React.ReactNode
  originalPrice: number
  currency: string
  priceValue: number
  discountOnlyFirstPurchase: boolean
}

function getDurationFrequency({
  duration,
  discount,
  selected,
  price,
  originalPrice,
  currency,
  priceValue,
}: DurationProps) {
  if (discount != null && discount > 0) {
    return (
      <>
        <p className="line-through">
          <FormattedCurrency value={originalPrice} currency={currency} />
        </p>
        <div
          className={cn(
            'ml-10 flex font-demibold',
            selected ? 'bg-green-electric' : 'bg-beige-muted text-black',
          )}
        >
          <FormattedMessage
            id="chooseSubscription.discountOff"
            defaultMessage="-{percent}%"
            values={{ percent: discount }}
          />
          <p className="pl-2">*</p>
        </div>
      </>
    )
  }
  if (duration === 1)
    return (
      <FormattedMessage
        id="upgrade.billed_monthly"
        defaultMessage="Save {amount} for the first month"
        values={{
          amount: (
            <FormattedCurrency
              value={originalPrice - priceValue}
              currency={currency}
            />
          ),
        }}
      />
    )
  if (duration === 3)
    return (
      <FormattedMessage
        id="upgrade.billed_quarterly"
        defaultMessage="Billed {price} quarterly"
        values={{ price }}
      />
    )
  if (duration === 6)
    return (
      <FormattedMessage
        id="upgrade.billed_twiceAYear"
        defaultMessage="Billed {price} twice annually"
        values={{ price }}
      />
    )
  if (duration === 12)
    return (
      <FormattedMessage
        id="upgrade.billed_yearly"
        defaultMessage="Billed {price} annually"
        values={{ price }}
      />
    )
  if (duration === 24)
    return (
      <FormattedMessage
        id="upgrade.billed_everyOtherYear"
        defaultMessage="Billed {price} every other year"
        values={{ price }}
      />
    )
}

type Props = {
  price: number
  pricePerMonth: number
  originalPrice: number
  duration: 1 | 3 | 6 | 12 | 24
  selected: boolean
  onClick: () => void
  currency: string
  bestPlanInfo: any
  trialDuration: number
  discount: number
  discountOnlyFirstPurchase: boolean
  isFallbackPrice: boolean
}

function ChooseSubscriptionProduct({
  price,
  pricePerMonth,
  originalPrice,
  duration,
  selected,
  onClick,
  currency,
  bestPlanInfo,
  trialDuration,
  discount,
  discountOnlyFirstPurchase = true,
  isFallbackPrice,
}: Props) {
  const inactiveTextStyle = !selected ? { color: '#AAA09B' } : undefined
  const durationText = getDurationFrequency({
    duration,
    discount,
    selected,
    originalPrice,
    currency,
    price: <FormattedCurrency value={price} currency={currency} />,
    priceValue: price ?? 0,
    discountOnlyFirstPurchase,
  })

  return (
    <div
      onClick={onClick}
      className="relative my-5 flex w-full min-w-full cursor-pointer items-center justify-center"
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-0 rounded-20 bg-white transition-transform duration-300 ease-in-out',
        )}
        style={{
          transform: selected ? 'scaleY(1)' : 'scaleY(0.9)',
          transformOrigin: 'center',
        }}
      />
      <div
        className={cn(
          `relative z-10 flex w-full flex-col justify-between rounded-20 border-0
        px-16 outline-none transition-all duration-300 ease-in-out focus:border-0 focus:outline-none focus:ring-0
        active:border-0 active:outline-none`,
          selected ? 'py-18' : 'py-16',
        )}
      >
        <div
          className={`relative flex w-full min-w-0 justify-between text-left lg:flex-row ${
            bestPlanInfo ? 'lg:mb-2' : 'lg:mb-7'
          }`}
        >
          <div className="min-w-0" style={inactiveTextStyle}>
            <div className="order-reverse">
              <ChooseSubscriptionText
                duration={duration}
                discount={discount}
                discountOnlyFirstPurchase={discountOnlyFirstPurchase}
                originalPrice={originalPrice}
                currency={currency}
                price={price}
                isFallbackPrice={isFallbackPrice}
                selected={selected}
                durationText={durationText}
              />
            </div>
          </div>
          <div
            className={cn(
              'min-w-0',
              bestPlanInfo ? 'self-start' : 'self-center',
            )}
          >
            {bestPlanInfo && React.cloneElement(bestPlanInfo, { selected })}
            <div className="float-right" style={inactiveTextStyle}>
              {pricePerMonth != null && (
                <div
                  className={cn(
                    'flex flex-wrap items-center justify-end text-right text-17 font-demibold transition-[margin-top] duration-300 ease-in-out',
                    bestPlanInfo && selected ? 'mt-16' : 'mt-12',
                  )}
                >
                  <FormattedCurrency
                    value={pricePerMonth}
                    currency={currency}
                  />
                  <span
                    className={cn(
                      'text-TypeSub',
                      selected ? 'text-black' : 'text-beige-muted',
                    )}
                  >
                    /
                    <FormattedMessage
                      id="SignUp.shortMon"
                      defaultMessage="mon"
                    />
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {Boolean(trialDuration) && (
          <div className="w-full text-left" style={inactiveTextStyle}>
            <span className="leading-1 text-14" data-uid="plan_free_trial_info">
              <FormattedMessage
                defaultMessage="{numberOfDays}-day free trial included"
                id="free_trial_50_check_2"
                values={{
                  numberOfDays: trialDuration,
                }}
              />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChooseSubscriptionProduct
